import React, { useMemo, useState } from "react";
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { format, isBefore, isSameDay, startOfDay } from "date-fns";
import { FloatingServiceSummary } from "../../components/floatingServiceSummary/FloatingServiceSummary";
import { useBookTime } from "../bookTime/useBookTime";
import { GenericSkeleton } from "../../components/skeleton/genericSkeleton";
import { MetaDescriptor } from "../../metaDescriptor/metaDescriptor";
import { useQuery } from "../../utils/useQuery";
import { useChangeService } from "../utils/useChangeService";

const formatTo12Hour = (time: string): string => {
  if (!time || typeof time !== "string") return "";
  if (time.includes("AM") || time.includes("PM")) return time;
  const [startPart] = time.split(" - ");
  const [hourStr, minute] = startPart.split(":");
  const hour = Number(hourStr);

  if (Number.isNaN(hour) || minute === undefined) return time;

  const period = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;

  return `${hour12}:${minute} ${period}`;
};

export const DateTimeSelection: React.FC = () => {
  const query = useQuery();
  const serviceIdParam = query.get("serviceId");

  const {
    updateCartWithTime,
    confirmTimeSelection,
    isFreshFlow,
    isLoading,
    selectedDate,
    setSelectedDate,
    selectedTimeSlot,
    setSelectedTimeSlot,
    currentShopTime,
    shop,
    cart,
    availableTimeSlots,
    navigateToService,
    navigateToStaff,
    account,
    showDualPricingModal,
    setShowDualPricingModal,
    onDualPricingPayBy,
  } = useBookTime();

  const [currentMonth, setCurrentMonth] = useState<Date>(
    () => selectedDate || currentShopTime || new Date()
  );

  const changeService = useChangeService();

  // Calendar-first flow is service -> time -> barber, so this screen's "back" is the
  // service step. Only the older barber-first flow (no date/time picked yet) goes
  // back to the barber; sending the calendar-first flow there looped barber <-> time
  // and the customer could never get back to change the service.
  const isCalendarFirst = isFreshFlow || (!!query.get("date") && !!query.get("time"));
  const navigateBack = isCalendarFirst ? navigateToService : navigateToStaff;

  const freshService = useMemo(() => {
    if (!account?.services || !serviceIdParam) return null;
    return account.services.find((s: any) => s.id === Number(serviceIdParam));
  }, [account, serviceIdParam]);

  const displayServices = useMemo(() => {
    if (cart?.merchantServices && cart.merchantServices.length > 0) {
      return cart.merchantServices.map((ms: any) => ({
        id: ms.id,
        title: ms.title || ms.businessService?.title,
        duration: ms.duration || ms.businessService?.duration,
        price:
          ms.price ||
          (ms.priceCents ? `$${(ms.priceCents / 100).toFixed(2)}` : ""),
      }));
    }
    if (freshService) {
      return [
        {
          id: freshService.id,
          title: freshService.title || freshService.name,
          duration: freshService.duration,
          price:
            freshService.price ||
            (freshService.priceCents
              ? `$${(freshService.priceCents / 100).toFixed(2)}`
              : ""),
        },
      ];
    }
    return [];
  }, [cart, freshService]);

  const floatingService = useMemo(() => {
    if (cart?.merchantServices && cart.merchantServices.length > 0) {
      return cart.merchantServices[0];
    }
    if (freshService) {
      return {
        ...freshService,
        title: freshService.title || freshService.name,
        priceCents:
          freshService.priceCents ||
          (freshService.price
            ? Math.round(
                parseFloat(freshService.price.replace(/[^0-9.]/g, "")) * 100
              )
            : 0),
      };
    }
    return null;
  }, [cart, freshService]);

  const selectedPractitioner = useMemo(() => {
    if (cart?.merchantServices?.[0]?.employee) {
      return cart.merchantServices[0].employee;
    }
    return {
      name: shop?.name || account?.name || "Provider",
      avatar: shop?.logo || account?.logo || "",
    };
  }, [cart, shop, account]);

  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const today = currentShopTime || new Date();

    for (let i = 0; i < 42; i++) {
      const date = new Date(
        startDate.getFullYear(),
        startDate.getMonth(),
        startDate.getDate() + i
      );

      const isCurrentMonth = date.getMonth() === month;
      const isToday = isSameDay(date, today);
      const isPast = isBefore(startOfDay(date), startOfDay(today));
      const isSelected = selectedDate ? isSameDay(date, selectedDate) : false;
      const isDisabled = isPast || !isCurrentMonth;

      days.push({
        date,
        day: date.getDate(),
        isCurrentMonth,
        isToday,
        isPast,
        isSelected,
        isDisabled,
      });
    }

    return days;
  };

  const navigateMonth = (direction: "prev" | "next") => {
    const newMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + (direction === "prev" ? -1 : 1),
      1
    );
    setCurrentMonth(newMonth);
  };

  const calendarDays = generateCalendarDays();
  const monthYear = currentMonth.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  const handleNext = async () => {
    if (isFreshFlow) {
      confirmTimeSelection();
    } else {
      await updateCartWithTime();
    }
  };

  if (!shop) {
    return (
      <div className="my-4 mobile:pt-0 mobile:pb-0">
        <GenericSkeleton />
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-slate-50 flex flex-col items-center">
      {account && (
        <MetaDescriptor
          title={account.name}
          description={account.description}
          link={window.location.href}
        />
      )}

      {/* Header */}
      <div className="bg-white border-b border-slate-200 w-full">
        <div className="px-4 py-3 max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <button
              onClick={navigateBack}
              className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <h1 className="text-lg font-medium text-slate-900">
              Select Date & Time
            </h1>
            <div className="w-8"></div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-4 w-full max-w-4xl">
        {/* Selected Service Summary */}
        {displayServices.length > 0 && (
          <div className="bg-white rounded-lg border border-slate-200 p-3 mb-4">
            <div className="flex justify-end mb-1">
              <button
                onClick={changeService}
                className="text-xs font-medium text-blue-600 hover:text-blue-700"
              >
                Change service
              </button>
            </div>
            {displayServices.map((ms: any) => (
              <div
                key={ms.id || ms.title}
                className="flex justify-between items-center"
              >
                <div>
                  <div className="font-medium text-slate-900 text-sm">
                    {ms.title}
                  </div>
                  <div className="text-xs text-slate-500">
                    {ms.duration} minutes
                  </div>
                </div>
                <div className="text-lg font-bold text-blue-600">{ms.price}</div>
              </div>
            ))}
          </div>
        )}

        {/* Calendar and Time Slots Side by Side */}
        <div className="grid lg:grid-cols-2 gap-4 mb-4">
          {/* Calendar */}
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={() => navigateMonth("prev")}
                className="p-1 hover:bg-slate-100 rounded transition-colors"
              >
                <ChevronLeft className="w-4 h-4 text-slate-600" />
              </button>
              <h3 className="font-medium text-slate-900 text-sm">{monthYear}</h3>
              <button
                onClick={() => navigateMonth("next")}
                className="p-1 hover:bg-slate-100 rounded transition-colors"
              >
                <ChevronRight className="w-4 h-4 text-slate-600" />
              </button>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {["S", "M", "T", "W", "T", "F", "S"].map((day) => (
                <div
                  key={day}
                  className="text-center text-xs font-medium text-slate-500 py-1"
                >
                  {day}
                </div>
              ))}

              {calendarDays.map((day, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setSelectedDate(day.date);
                    setSelectedTimeSlot(undefined);
                  }}
                  disabled={day.isDisabled}
                  className={`aspect-square flex items-center justify-center text-xs font-medium rounded transition-colors ${
                    day.isSelected
                      ? "bg-blue-600 text-white"
                      : day.isToday
                      ? "bg-blue-100 text-blue-600"
                      : day.isDisabled
                      ? "text-slate-300 cursor-not-allowed"
                      : "text-slate-900 hover:bg-slate-100"
                  }`}
                >
                  {day.day}
                </button>
              ))}
            </div>
          </div>

          {/* Time Slots */}
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <h3 className="font-medium text-slate-900 mb-3 text-sm">
              {selectedDate ? "Available Times" : "Select a date to see times"}
            </h3>
            {selectedDate ? (
              <div className="grid grid-cols-3 gap-2 max-h-80 overflow-y-auto">
                {isLoading && (
                  <div className="col-span-3 text-center py-6 text-slate-400 text-xs">
                    Loading time slots...
                  </div>
                )}
                {!isLoading && availableTimeSlots?.length === 0 && (
                  <div className="col-span-3 text-center py-8 text-slate-400 text-xs">
                    No available time slots
                  </div>
                )}
                {!isLoading &&
                  availableTimeSlots?.map((slot) => (
                    <button
                      key={slot}
                      onClick={() => setSelectedTimeSlot(slot)}
                      className={`p-2 rounded border text-center transition-all duration-200 text-xs font-medium ${
                        slot === selectedTimeSlot
                          ? "border-blue-600 bg-blue-600 text-white"
                          : "border-slate-200 hover:border-blue-300 hover:bg-blue-50 text-slate-700"
                      }`}
                    >
                      {formatTo12Hour(slot)}
                    </button>
                  ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <div className="text-sm">Please select a date first</div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom padding for floating popup */}
        <div className="h-24"></div>
      </div>

      {/* Floating Service Summary */}
      <FloatingServiceSummary
        selectedService={floatingService}
        selectedAddonServices={cart?.merchantServices?.slice(1) || []}
        selectedPractitioner={selectedPractitioner}
        onNext={handleNext}
        showNextButton={!!(selectedDate && selectedTimeSlot)}
      />
    </div>
  );
};
